import { useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Plus, Minus, MapPin, Phone, Mail, Clock, MessageCircle,
  Facebook, Instagram, Youtube, Linkedin, Quote } from "lucide-react";
import { Reveal } from "../motion";
import { SERVICES, DESTINATIONS, WHY, STEPS, TESTIMONIALS, FAQS, CONTACT, IMG, LOGO } from "../../lib/data";

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const Overline = ({ children }) => <p className="text-xs uppercase tracking-[0.25em] text-teal font-bold mb-4">{children}</p>;

/* ---------------- Marquee ---------------- */
export const TrustMarquee = () => (
  <div className="bg-primary-blue py-6 border-y border-white/10" data-testid="trust-marquee">
    <Marquee gradient={false} speed={40}>
      {["Canada", "Australia", "New Zealand", "United Kingdom", "Germany", "Finland", "Poland", "Slovakia", "Malta", "Luxembourg", "Italy", "Romania"].map((c) => (
        <span key={c} className="font-serifd text-2xl md:text-3xl text-white/40 italic mx-10">{c}</span>
      ))}
    </Marquee>
  </div>
);

/* ---------------- About ---------------- */
export const About = () => (
  <section id="about" className="py-24 md:py-32 bg-off-white" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-5">
        <div className="relative">
          <img src={IMG.consult} alt="Immigration consultant guiding a client" className="rounded-2xl w-full object-cover aspect-[4/5] shadow-xl" />
          <div className="absolute -bottom-6 -right-4 md:right-6 bg-primary-blue text-white rounded-2xl p-6 max-w-[220px] shadow-lg">
            <p className="font-serifd text-4xl text-gold">01</p>
            <p className="text-sm text-slate-200 mt-1">A trusted partner for your international journey.</p>
          </div>
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal><Overline>About Global Connect</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-midnight">
            Guiding you through global education, immigration & careers.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-slate-600 md:text-lg leading-relaxed max-w-2xl">
            Global Connect helps clients understand and navigate international education, immigration, employment, and visa processes — with clear, honest guidance at every step. Immigration and visa decisions are made by the relevant government authorities; we provide consultancy and application assistance.
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {[["Our Mission", "To connect people with genuine international opportunities through transparent, professional guidance."],
            ["Our Vision", "To be a trusted global consultancy known for integrity, clarity, and dedicated client support."]].map(([t, d], i) => (
            <Reveal key={t} delay={0.1 * i}>
              <div className="rounded-xl border border-slate-200 bg-white p-6 h-full">
                <h3 className="font-serifd text-2xl text-primary-blue">{t}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-wrap gap-3">
            {["Professional Guidance", "Transparent Communication", "Personalized Assistance", "Application Support"].map((t) => (
              <span key={t} className="rounded-full border border-teal/30 bg-teal/5 text-teal text-sm px-4 py-2 font-medium">{t}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Services ---------------- */
export const Services = () => (
  <section id="services" className="py-24 md:py-32 bg-white" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><Overline>Our Services</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight max-w-3xl leading-tight">
          End-to-end support across every pathway.
        </h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={0.08 * i}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-off-white p-8 hover:-translate-y-1 hover:border-gold transition-all duration-300">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-teal font-bold">{s.tag}</span>
                <span className="font-serifd text-3xl text-slate-200 group-hover:text-gold transition-colors">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-serifd text-3xl text-primary-blue">{s.title}</h3>
              <ul className="mt-5 space-y-2.5">
                {s.items.map((it) => (
                  <li key={it} className="flex items-center gap-2.5 text-slate-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />{it}
                  </li>
                ))}
              </ul>
              <button onClick={() => go("consultation")} data-testid={`service-${s.id}-learn-more`}
                className="mt-7 inline-flex items-center gap-2 text-primary-blue font-semibold group-hover:text-teal transition-colors">
                Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Destinations ---------------- */
export const Destinations = () => (
  <section id="destinations" className="py-24 md:py-32 bg-off-white" data-testid="destinations-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Reveal><Overline>Countries & Destinations</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight max-w-2xl leading-tight">
              Explore opportunities around the world.
            </h2>
          </Reveal>
        </div>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {DESTINATIONS.map((d, i) => (
          <Reveal key={d.name} delay={0.05 * (i % 3)}>
            <div className="group relative overflow-hidden rounded-2xl aspect-[4/5]" data-testid={`destination-${d.name.toLowerCase().replace(/ /g, '-')}`}>
              <img src={d.img} alt={`${d.name} — overseas opportunities`} loading="lazy"
                className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,20,40,0.92) 8%, rgba(10,20,40,0.15) 55%, transparent)" }} />
              <div className="relative z-10 h-full flex flex-col justify-end p-6 text-white">
                <h3 className="font-serifd text-3xl">{d.name}</h3>
                <p className="text-sm text-slate-200 mt-1">{d.desc}</p>
                <p className="text-xs text-gold mt-2 font-medium">{d.ops}</p>
                <button onClick={() => go("consultation")} data-testid={`explore-${d.name.toLowerCase().replace(/ /g, '-')}`}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-gold transition-colors self-start">
                  Explore Country <ArrowUpRight size={15} />
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-sm text-slate-500 max-w-2xl">Destinations shown are indicative. Availability of pathways depends on eligibility and the rules of the relevant government authorities.</p>
    </div>
  </section>
);

/* ---------------- Why Choose Us ---------------- */
export const WhyChooseUs = () => (
  <section id="why" className="py-24 md:py-32 bg-midnight grain relative overflow-hidden" data-testid="why-section">
    <div className="absolute inset-0" style={{ background: "radial-gradient(70% 50% at 15% 20%, rgba(15,118,110,0.2), transparent 60%)" }} />
    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-4">Why Choose Us</p></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-white max-w-3xl leading-tight">
          A consultancy built on trust and transparency.
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {WHY.map((w, i) => {
          const Icon = Icons[w.icon] || Icons.Check;
          return (
            <Reveal key={w.title} delay={0.05 * i}>
              <div className="group h-full rounded-2xl border border-white/10 bg-white/5 p-6 hover:border-gold/50 hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-xl bg-gold/15 grid place-items-center text-gold group-hover:bg-gold group-hover:text-midnight transition-colors">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 text-white font-semibold text-lg">{w.title}</h3>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- How It Works ---------------- */
export const HowItWorks = () => (
  <section id="process" className="py-24 md:py-32 bg-white" data-testid="process-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><Overline>How It Works</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight max-w-2xl leading-tight">A clear, five-step journey.</h2>
      </Reveal>
      <div className="mt-16 relative">
        <div className="absolute left-0 top-8 hidden lg:block h-px w-full bg-slate-200" />
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.1 * i}>
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-primary-blue text-white grid place-items-center font-serifd text-2xl relative z-10">{s.n}</div>
                <h3 className="mt-6 font-semibold text-lg text-midnight">{s.t}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials ---------------- */
export const Testimonials = () => (
  <section className="py-24 md:py-32 bg-off-white" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><Overline>Success Stories</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight max-w-2xl leading-tight">What our clients say.</h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={0.06 * i}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 hover:border-teal transition-colors">
              <Quote className="text-gold" size={28} />
              <p className="mt-4 text-slate-700 leading-relaxed">{t.q}</p>
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="font-semibold text-midnight">{t.name}</p>
                <p className="text-sm text-slate-500">{t.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-sm text-slate-500 max-w-3xl">Testimonials represent individual experiences and do not guarantee similar outcomes. Placeholder reviews shown here can be replaced with verified client reviews.</p>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <Reveal><Overline>Frequently Asked Questions</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight leading-tight">Answers before you begin.</h2>
        </Reveal>
        <div className="mt-12 divide-y divide-slate-200 border-t border-slate-200">
          {FAQS.map((f, i) => (
            <div key={i} className="py-2">
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-4 py-5 text-left">
                <span className="font-medium text-lg text-midnight">{f.q}</span>
                {open === i ? <Minus className="text-teal shrink-0" size={20} /> : <Plus className="text-primary-blue shrink-0" size={20} />}
              </button>
              <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                className="overflow-hidden">
                <p className="pb-6 text-slate-600 leading-relaxed">{f.a}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Contact ---------------- */
export const Contact = () => (
  <section id="contact" className="py-24 md:py-32 bg-off-white" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
      <div>
        <Reveal><Overline>Contact Us</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl font-light text-midnight leading-tight">Let's talk about your journey.</h2>
        </Reveal>
        <div className="mt-10 space-y-5">
          {[[MapPin, "Office Address", CONTACT.address], [Phone, "Phone", CONTACT.phone],
            [MessageCircle, "WhatsApp", CONTACT.whatsapp], [Mail, "Email", CONTACT.email],
            [Clock, "Business Hours", CONTACT.hours]].map(([Icon, label, val]) => (
            <Reveal key={label}>
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-primary-blue/10 grid place-items-center text-primary-blue shrink-0"><Icon size={20} /></div>
                <div><p className="text-sm text-slate-500">{label}</p><p className="text-midnight font-medium">{val}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 flex gap-4">
          <button onClick={() => go("consultation")} data-testid="contact-consult-btn" className="rounded-full bg-primary-blue text-white px-7 py-3.5 font-semibold hover:bg-teal transition-colors">Book a Consultation</button>
          <a href={`https://wa.me/${CONTACT.whatsappLink}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="rounded-full border border-slate-300 px-7 py-3.5 font-semibold text-midnight hover:border-teal transition-colors">WhatsApp Us</a>
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="rounded-2xl overflow-hidden border border-slate-200 h-full min-h-[420px]" data-testid="contact-map">
          <iframe title="Global Connect location" width="100%" height="100%" loading="lazy" style={{ border: 0, minHeight: 420 }}
            src="https://www.google.com/maps?q=SCO+1040+TDI+City+Connaught+Plaza+Airport+Road+Mohali+160055&output=embed" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Footer ---------------- */
const FOOTER_LINKS = [["Home", "home"], ["About Us", "about"], ["Services", "services"], ["Destinations", "destinations"], ["Careers", "consultation"], ["FAQs", "faq"], ["Contact Us", "contact"]];
const SOCIAL = [[Facebook, "Facebook"], [Instagram, "Instagram"], [Youtube, "YouTube"], [Linkedin, "LinkedIn"], [MessageCircle, "WhatsApp"]];

export const Footer = () => (
  <footer className="bg-midnight text-white pt-24 pb-10 grain relative overflow-hidden" data-testid="site-footer">
    <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(212,175,55,0.1), transparent 60%)" }} />
    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <h2 className="font-serifd text-5xl md:text-7xl font-light leading-tight">Start Your Global Journey</h2>
        <button onClick={() => go("consultation")} data-testid="footer-consult-btn"
          className="mt-8 rounded-full bg-gold text-midnight px-9 py-4 font-semibold hover:bg-white transition-colors">Book a Consultation</button>
      </div>
      <div className="mt-20 grid md:grid-cols-4 gap-10 border-t border-white/10 pt-14">
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl inline-block p-3"><img src={LOGO} alt="Global Connect logo" className="h-14 w-auto object-contain" /></div>
          <p className="font-serifd text-2xl text-gold mt-5 italic">Connecting You to Global Opportunities</p>
          <p className="mt-4 text-slate-400 text-sm max-w-md leading-relaxed">Global Connect provides immigration, visa, overseas education and international recruitment consultancy and application assistance. Visa and immigration decisions are made by the relevant government authorities.</p>
          <div className="mt-6 flex gap-3">
            {SOCIAL.map(([Icon, name]) => (
              <a key={name} href="#" aria-label={name} data-testid={`social-${name.toLowerCase()}`}
                className="h-10 w-10 rounded-full border border-white/15 grid place-items-center text-slate-300 hover:bg-gold hover:text-midnight hover:border-gold transition-colors"><Icon size={17} /></a>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm uppercase tracking-widest text-slate-400 mb-5">Explore</p>
          <ul className="space-y-3">
            {FOOTER_LINKS.map(([label, id]) => (
              <li key={label}><button onClick={() => go(id)} data-testid={`footer-${id}-link`} className="text-slate-300 hover:text-gold transition-colors">{label}</button></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-sm uppercase tracking-widest text-slate-400 mb-5">Legal</p>
          <ul className="space-y-3 text-slate-300">
            <li><a href="#" className="hover:text-gold transition-colors">Privacy Policy</a></li>
            <li><a href="#" className="hover:text-gold transition-colors">Terms & Conditions</a></li>
            <li><a href="#" className="hover:text-gold transition-colors">Disclaimer</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-sm text-slate-500">
        <p>© {new Date().getFullYear()} Global Connect. All rights reserved.</p>
        <p>Consultancy & application assistance · Outcomes decided by relevant authorities.</p>
      </div>
    </div>
  </footer>
);
